(ns app.front.view.group.admin.config-selectors
  "Checkbox selectors operating on a configuration branch"
  (:require [app.domain.group.config.model :as config-model]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.property.model :as property-model]
            [app.domain.review.model :as review-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.common-tags :as ct]
            [app.front.ui.config-pills :as ui-config-pills]
            [app.front.util :as futil :refer [for-frag]]
            [app.front.view.attachment.common :as attachment-common]
            [app.front.view.conditions.common :as condition-common]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.config.form-schema-subs :as cfs-subs]
            [app.front.view.group.admin.inspections.closure-categories :as closure-categories]
            [app.front.view.group.admin.review-config :as group.admin.review-config]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn roles-add-i18n [tr lang roles]
  (->> roles
       (map (fn [role]
              (if (keyword? role)
                [role
                 (tr [role])]
                [(:db/id role)
                 (i18n-model/get-text-from-entities-or-map (:party-role/names role) lang)])))
       (sort-by second)))

(rf/reg-sub ::roles-with-i18n
  :<- [:tr]
  :<- [:language]
  :<- [::gas/party-roles]
  (fn [[tr language party-roles] _]
    (->> party-roles
         (roles-add-i18n tr language))))

(defn entity-in-fn [entities]
  (let [entity-set (set entities)]
    #(contains? entity-set (or (:db/id %) %))))

(rf/reg-sub ::selectable-party-roles
  :<- [::gas/party-roles]
  (fn [all-roles [_ enabled-roles]]
    (->> all-roles
         (filter (entity-in-fn enabled-roles)))))

(rf/reg-sub ::available-roles-with-i18n
  :<- [:tr]
  :<- [:language]
  :<- [::gas/party-roles]
  (fn [[tr language party-roles] [_ enabled-roles]]
    (->> party-roles
         (filter (entity-in-fn enabled-roles))
         (roles-add-i18n tr language))))

(defn review-config-title [review-config]
  (let [language (<sub [:language])]
    (str (review-model/review-config-name review-config language)
         (when-some [integration-code (:review.integration/code review-config)]
           (str " ❘ " integration-code)))))


(defn checkbox-props [{:keys [target tr parent-config disabled?]} id value label-fn current-opts parent-opts config-key opt]
  {:id         (str target "/" id)
   :label-text (or (label-fn opt) "")
   :disabled   (boolean (or (contains? parent-opts value)
                            disabled?))
   :title      (if (contains? parent-opts id)
                 (tr [:group-admin/disabled-reason]
                     [(get-in parent-config [config-key id])])
                 "")
   :checked    (contains? (set/union current-opts
                                     parent-opts)
                          value)})


(defn pick-items-group-editor [{:keys [tr *group-config parent-config]}
                               value-fn
                               val->str
                               values
                               config-key
                               & [opts]]
  [ui-config-pills/config-pills
   (merge (util/mmap {:id           (str "config-" (name config-key))
                      :selected     (->> values
                                         (filter (comp (set/difference (set (config-key @*group-config))
                                                                       (set (:pre-selected opts)))
                                                       value-fn)))
                      :pre-selected (->> values
                                         (filter (comp (set/union (set (keys (config-key parent-config)))
                                                                  (set (:pre-selected opts)))
                                                       value-fn)))
                      :legend-text  (tr [(or (:alt-legend opts) config-key)])
                      :add-item     (fn [value]
                                      (swap! *group-config update config-key conj (value-fn value)))
                      :remove-item  (fn [value]
                                      (swap! *group-config update config-key disj (value-fn value)))}
                     tr val->str values)
          (dissoc opts :pre-selected))])


(defn toggle-items-group-editor [{:keys [tr *group-config parent-config]}
                                 value-fn
                                 val->str
                                 values
                                 config-key
                                 & [opts]]
  (let [selected-values (set/difference (set (config-key @*group-config))
                                        (set (:pre-selected opts)))
        disabled-values (set/union (set (keys (config-key parent-config)))
                                   (set (:pre-selected opts)))]
    [:div
     [:label.bx--label (tr [(or (:alt-legend opts) config-key)])]
     [:div.flex.flex-row.flex-wrap.gap-2.mb-2
      (for-frag [value values]
        (let [selected? (contains? selected-values (value-fn value))
              disabled? (contains? disabled-values (value-fn value))]
          [ct/tag {:label-text (val->str value)
                   :selected?  (and (not disabled?)
                                    (cond-> selected?
                                      (:invert? opts) not))
                   :disabled?  disabled?
                   :on-click   #(swap! *group-config update config-key (if selected? disj conj) (value-fn value))}]))]
     (when-not (empty? disabled-values)
       [ui/help (tr [:group-admin/toggle-editor-disabled-help])])]))


(defn form-schema-editor [editor-ctx values]
  (when (seq values)
    [pick-items-group-editor
     editor-ctx
     :db/id
     #(or (:form.schema/config-name %)
          (:form.schema/name %))
     values
     :group.config/form-schemas]))

(defn pre-consultation-form-schema-editor [editor-ctx]
  [pick-items-group-editor
   editor-ctx
   :db/id
   :form.schema/name
   (<sub [::cfs-subs/pre-consultation-schemas])
   :group.config/pre-consultation-form-schemas])

(rf/reg-sub ::all-inspection-visit-result-details
  :<- [::gas/group-id]
  :<- [::entities/context]
  :<- [:language]
  :<- [::gas/group-tr]
  :<- [::ff/enabled? :feature-flag.id/inspection-visit-configuration]
  (fn [[group-id ctx lang tr show-failure-reasons?]]
    (->> (entities/find-entities ctx {:domain/authority-group group-id
                                      :domain/state           :visit-result-detail.state/active
                                      :entity/type            :entity.type/visit-result-detail})
         ((inspection-model/filter-applicable-reasons-fn show-failure-reasons?))
         (map (fn [{id :db/id :visit-result-detail/keys [visit-result result] :as e}]
                (util/mmap {:display-name (str (or (inspection-model/visit-result-name visit-result lang)
                                                   (tr [result]))
                                               " / "
                                               (inspection-model/visit-result-detail-name e lang))}
                           id)))
         (sort-by :display-name))))

(defn inspection-result-detail-editor [editor-ctx]
  [pick-items-group-editor
   editor-ctx
   :id
   :display-name
   (<sub [::all-inspection-visit-result-details])
   :group.config/inspection-visit-result-details
   {:empty-placeholder :group.config/no-inspection-visit-result-details
    :alt-legend        (when (:reason-conf? editor-ctx) :group.config/inspection-visit-result-reasons)}])

(rf/reg-sub ::all-label-groups
  :<- [::gas/group-id]
  :<- [::entities/context]
  (fn [[group-id ctx]]
    (->> (entities/find-entities ctx {:domain/authority-group group-id
                                      :domain/state           :label-group.state/active
                                      :entity/type            :entity.type/label-group})
         (map (fn [{id :db/id :label-group/keys [name]}]
                {:id           id
                 :display-name name}))
         (sort-by :display-name))))

(defn label-groups-editor [editor-ctx]
  (when (<sub [::ff/enabled? :feature-flag.id/inspection-labels])
    [pick-items-group-editor
     editor-ctx
     :id
     :display-name
     (<sub [::all-label-groups])
     :group.config/label-groups
     {:empty-placeholder :group.config/no-label-groups}]))

(rf/reg-sub ::attachment-types
  (fn [[_ permit-type]]
    [(rf/subscribe [::gas/root-group-config permit-type])
     (rf/subscribe [:language])])
  (fn [[root-config lang] _]
    (->> root-config
         :group.config/available-attachments
         (sort-by #(attachment-common/attachment-type-label % lang)))))

(rf/reg-sub ::attribute-templates
  :<- [::entities/context]
  :<- [::gas/group]
  (fn [[ctx group] _]
    (->> (entities/find-entities ctx {:property-attribute-template/group (-> group :entity/parent :db/id)
                                      :domain/state                      :property-attribute-template.state/enabled})
         (filter (fn [attr]
                   (contains? (set (:property-attribute-template/visible-in-groups attr)) group)))
         (sort-by :ordered/order))))


(defn attachments-editor [{:keys [permit-type] :as editor-ctx}]
  (let [lang (<sub [:language])]
    [pick-items-group-editor
     editor-ctx
     :db/id
     #(attachment-common/attachment-type-label % lang)
     (<sub [::attachment-types permit-type])
     :group.config/required-attachments]))

(defn property-attr-editor [editor-ctx]
  [toggle-items-group-editor
   editor-ctx
   :db/id
   property-model/attribute-template-name
   (<sub [::attribute-templates])
   :group.config/hidden-property-attribute-templates
   {:invert? true}])


(defn get-enabled-roles [attr {:keys [*group-config parent-config]}]
  (concat (keys (get parent-config attr))
          (get @*group-config attr)))

(defn get-selected-roles [config-key {:keys [*group-config]}]
  (-> @*group-config config-key))

(defn req-roles-editor [editor-ctx]
  [pick-items-group-editor
   editor-ctx
   first
   last
   (<sub [::available-roles-with-i18n
          (concat (get-enabled-roles :group.config/applicable-party-roles editor-ctx)
                  (get-selected-roles :group.config/required-party-roles editor-ctx))])
   :group.config/required-party-roles])

(defn req-roles-issuance-editor [editor-ctx]
  [pick-items-group-editor
   editor-ctx
   first
   last
   (<sub [::available-roles-with-i18n
          (concat (get-enabled-roles :group.config/applicable-party-roles editor-ctx)
                  (get-selected-roles :group.config/required-party-roles-before-issuance editor-ctx))])
   :group.config/required-party-roles-before-issuance])

(defn applicable-roles-editor [editor-ctx]
  [pick-items-group-editor
   editor-ctx
   first
   last
   (filter (comp not keyword? first) (<sub [::roles-with-i18n]))
   :group.config/applicable-party-roles
   {:pre-selected (concat (get-selected-roles :group.config/required-party-roles editor-ctx)
                          (get-selected-roles :group.config/required-party-roles-before-issuance editor-ctx))}])


(defn pre-consultation-attachments-editor [{:keys [permit-type] :as editor-ctx}]
  (let [lang (<sub [:language])]
    [pick-items-group-editor
     editor-ctx
     :db/id
     #(attachment-common/attachment-type-label % lang)
     (<sub [::attachment-types permit-type])
     :group.config/pre-consultation-required-attachments]))


(defn pre-consultation-req-roles-editor [editor-ctx]
  [pick-items-group-editor
   editor-ctx
   first
   last
   (<sub [::available-roles-with-i18n
          (concat (get-enabled-roles :group.config/applicable-party-roles editor-ctx)
                  (get-selected-roles :group.config/pre-consultation-required-party-roles editor-ctx))])
   :group.config/pre-consultation-required-party-roles])

(rf/reg-sub ::party-role-ids-for-declaration-ids
  :<- [::gas/declaration-configs]
  (fn [declaration-configs [_ declaration-ids]]
    (->> declaration-configs
         (filter #(contains? declaration-ids (:db/id %)))
         (map :declaration-config/party-role)
         du/entity-id-set)))

(defn sign-off-req-roles-editor
  "At least one role must be selected"
  [{:keys [tr *group-config parent-config] :as ctx}]
  (let [id-fn            :db/id
        value-fn         :db/id
        label-fn         :declaration-config/name
        avail-opts       (<sub [::gas/declaration-configs])
        config-key       :group.config/sign-off-declarations
        current-opts     (config-key @*group-config)
        parent-opts      (-> parent-config config-key keys set)
        all-opts         (set/union parent-opts current-opts)
        selected-roles   (<sub [::party-role-ids-for-declaration-ids all-opts])
        enabled-role-ids (du/entity-id-set
                           (<sub [::selectable-party-roles (get-enabled-roles
                                                             :group.config/applicable-party-roles
                                                             ctx)]))]
    [acommon/form-group {:legend-text (tr [config-key])}
     [:<>
      (for-frag [opt avail-opts
                 :let [id        (id-fn opt)
                       value     (value-fn opt)
                       ;; checkbox is disabled if it is the only checkbox that is selected
                       disabled? (= all-opts
                                    #{value})
                       ctx       (assoc ctx :disabled? disabled?)]]
        (cc/checkbox
          (assoc (checkbox-props ctx id value label-fn current-opts parent-opts config-key opt)
                 :on-change #(swap! *group-config update config-key (if % conj disj) value))))
      (when-not (empty? (set/difference selected-roles enabled-role-ids))
        [ui/warning (tr [:group-admin/role-not-applicable])])
      (when (empty? all-opts)
        [ui/warning (tr [:group-admin/sign-off-declaration-required])])]]))


(defn review-config-editor
  ([editor-ctx]
   (review-config-editor editor-ctx :group.config/review-configs nil))
  ([{:keys [group-id *group-config parent-config target] :as editor-ctx} config-key alt-legend]
   (let [available-review-configs (<sub [::group.admin.review-config/available-review-configs group-id])
         final-review-config-ids  (<sub [::group.admin.review-config/available-final-review-config-ids group-id])
         child-has-final-review?  (->> (<sub [::gas/child-group-configs-sorted target])
                                       (mapcat config-key)
                                       (filter :review.config/final-review?)
                                       seq)
         applied-final-config-id  (or (some (config-key @*group-config #{}) final-review-config-ids)
                                      (some (-> parent-config config-key keys set) final-review-config-ids))
         review-configs           (cond->> available-review-configs
                                    (or applied-final-config-id child-has-final-review?)
                                    (remove :review.config/final-review?)

                                    (and applied-final-config-id (not child-has-final-review?))
                                    (cons (p/find-first available-review-configs {:db/id applied-final-config-id}))

                                    :always
                                    (sort-by :ordered/order))]
     [pick-items-group-editor
      editor-ctx
      :db/id
      review-config-title
      review-configs
      config-key
      (cond-> {}
        alt-legend (assoc :alt-legend alt-legend))])))

(defn submitted-review-config-editor [editor-ctx]
  [review-config-editor
   editor-ctx
   :group.config/submitted-review-configs
   :group-admin/default-reviews-during-submitted-step])


(defn- conditions-section-enabled?
  [{:keys [*group-config parent-config]}]
  (let [attr               :group.config/conditions-in-verdict-template?
        enabled-in-parent? (-> parent-config attr boolean)]
    (->> enabled-in-parent?
         (vector (attr @*group-config))
         (some true?)
         boolean)))

(defn conditions-verdict-config
  [{:keys [tr group-id] :as editor-ctx}]
  (when (<sub [::condition-common/conditions-enabled-for-group? group-id])
    [:div.md:flex
     (cc/form-group
       {:legend-text (tr [:group-admin/conditions-in-verdict-template])}
       [acommon/checkbox editor-ctx :group.config/conditions-in-verdict-template?])]))

(defn licence-renewal-day-limit
  "Defines how many days before the expiration a license can be renewed (earliest renewal point)"
  [{:keys [tr *group-config parent-config group-id]}]
  (let [attr         :group.config/licence-renewal-limit
        parent-value (some-> parent-config (get attr) vals last)]
    [:div.my-6
     [:div.md:flex
      [ui/number-input {:id            (str group-id "-renewal-limit")
                        :label-text    (tr [:group-admin/licence-renewal-limit])
                        :default-value (or (get @*group-config attr) parent-value)
                        :on-change     #(swap! *group-config assoc attr (some-> %
                                                                                futil/tval
                                                                                str-util/blank->nil
                                                                                js/parseInt))}]]
     [ui/help (tr [:group-admin/licence-renewal-limit-help])]]))


(defn general-conditions-editor
  [{:keys [group-id] :as editor-ctx}]
  (when (and (<sub [::condition-common/general-conditions-enabled-for-group? group-id])
             ;; General conditions are only used in the conditions section of the permit template
             (conditions-section-enabled? editor-ctx))
    [pick-items-group-editor
     editor-ctx
     :db/id
     :application.condition.template/title
     (<sub [::condition-common/templates-for-group group-id])
     :group.config/general-conditions]))


(defn default-conditions-editor
  [{:keys [group-id] :as editor-ctx}]
  (when (<sub [::condition-common/default-conditions-enabled-for-group? group-id])
    [pick-items-group-editor
     editor-ctx
     :db/id
     :application.condition.template/title
     (<sub [::condition-common/templates-for-group group-id])
     :group.config/default-conditions]))

(defn supplemental-permit-editor
  "Given a permit type, display all available supplemental permit types and their categories"
  [{:keys [permit-type] :as editor-ctx}]
  (let [lang (<sub [:language])]
    (when-let [root-group-configs (<sub [::gas/supplemental-permit-type-root-configs permit-type])]
      [:<>
       (for-frag [root-config root-group-configs]
         (when-let [categories (seq (<sub [::gas/child-group-configs-sorted (:db/id root-config)]))]
           [pick-items-group-editor
            editor-ctx
            (fn [category-config]
              ;; XXX: These are referenced in FSMs by keywords and thus cannot really be converted to just config ids,
              ;; which means that supplemental configs without :group.config/target cannot currently be supported.
              [(:group.config/target root-config)
               (:group.config/target category-config)])
            #(config-model/localized-name % lang)
            categories
            :group.config/supplemental-permits]))])))

(defn closure-categories-editor [{:keys [group-id] :as editor-ctx}]
  (let [lang (<sub [:language])]
    [pick-items-group-editor
     editor-ctx
     :id
     #(i18n-model/get-text-from-entities-or-map (:name %) lang)
     (<sub [::closure-categories/closure-categories group-id])
     :group.config/close-without-inspection-categories]))

(defn grace-period-config
  "Defines after how many days a penalty or interest fee can be added."
  [{:keys [tr *group-config parent-config group-id]}]
  (let [attr         :group.config/grace-period
        parent-value (some-> parent-config (get attr) vals last)]
    [:div.my-6
     [:div.md:flex
      [ui/number-input {:id            (str group-id "-grace-period")
                        :label-text    (tr [:group-admin/grace-period])
                        :default-value (or (get @*group-config attr) parent-value)
                        :on-change     #(swap! *group-config assoc attr (some-> %
                                                                                futil/tval
                                                                                str-util/blank->nil
                                                                                js/parseInt))}]]
     [ui/help (tr [:group-admin/grace-period-help])]]))
